import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './FloatingSearchIcon.css';

const FloatingSearchIcon = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [position, setPosition] = useState({ x: null, y: null });
  const isDragging = useRef(false);
  const dragStartCount = useRef(0);
  const elementRef = useRef(null);
  const [user, setUser] = useState(null);

  // Track long press/drag vs click
  const dragStartTime = useRef(0);

  // Get user from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("tokenly_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, [location]); // Re-check when location changes (login/logout)

  useEffect(() => {
    // Initialize position on mount to ensure it stays within view if needed
    // or just let CSS handle initial state until moved
  }, []);

  const handlePointerDown = (e) => {
    // Only left click or touch
    if (e.type === 'mousedown' && e.button !== 0) return;

    e.preventDefault(); // Prevent text selection/scrolling on some devices

    isDragging.current = false;
    dragStartTime.current = Date.now();

    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;

    const rect = elementRef.current.getBoundingClientRect();

    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;

    const handlePointerMove = (moveEvent) => {
      const moveClientX = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveClientY = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientY : moveEvent.clientY;

      // If moved more than small threshold, it's a drag
      if (!isDragging.current) {
        const dx = moveClientX - clientX;
        const dy = moveClientY - clientY;
        if (Math.sqrt(dx * dx + dy * dy) > 5) {
          isDragging.current = true;
        }
      }

      if (isDragging.current) {
        // Update position
        let newX = moveClientX - offsetX;
        let newY = moveClientY - offsetY;

        // Boundary checks (optional but good)
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        newX = Math.max(0, Math.min(newX, maxX));
        newY = Math.max(0, Math.min(newY, maxY));

        setPosition({ x: newX, y: newY });
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);
  };

  const handleClick = (e) => {
    if (isDragging.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigate('/alldoctors');
  };

  // Inline styles to override CSS when dragged
  const style = position.x !== null
    ? { left: `${position.x}px`, top: `${position.y}px`, right: 'auto', bottom: 'auto', transform: 'none' }
    : {};

  // Don't render if user is a doctor or staff (role_id == 3 or 4)
  if (user?.roleId === 3 || user?.roleId === 4) {
    return null;
  }

  return (
    <>
      {/* Only render for non-doctor/staff users (patients and logged-out users) */}
      {(user?.roleId !== 3 && user?.roleId !== 4) && (
        <div
          ref={elementRef}
          className="floating-search-icon"
          onClick={handleClick}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
          style={style}
        >
          <div className="search-icon-wrapper">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>
          <span className="search-text">Find Doctor</span>
        </div>
      )}
    </>
  );
};

export default FloatingSearchIcon;
